import {ArrowDownward, ArrowUpward, Delete, FileCopy, MoreVert} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import {INCIDENT_SUBTABLE_KIND_EDITOR_OPTIONS, getIncidentSubTableDef} from 'src/modules/incident/incidentSubTable';
import {isIncidentLinkedClasificacion} from 'src/modules/shared/clasificacionIncidentLinked';
import CustomAccordion from 'src/view/shared/CustomAccordion';
import CheckboxFormItem from 'src/view/shared/form/items/CheckboxFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import Message from 'src/view/shared/message';
import ConfirmModal from 'src/view/shared/modals/ConfirmModal';
import SubseccionesPreguntasFormularioForm from './SubseccionesPreguntasFormularioForm';

const OnHoverBox = styled(Box)`
  display: flex;
  flexdirection: row;
  gap: 8px;
  opacity: 0.2;
  &:hover {
    opacity: 1;
  }
`;

/** Pasar a false para ofrecer la tabla de entrevistas en todos los tenants. */
const hideBlumarStuffForOthers = true;
const BLUMAR_SSO_TENANT_ID = '62a8b6dd2323505723b7e92b';

const SUBSECTION_CLIPBOARD_TYPE = 'planiwise.formSubsection';
const SUBSECTION_CLIPBOARD_VERSION = 1;

const generateClipboardId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
export default function SubseccionesFormularioForm(props) {
  const {categoriaIndex, name, esRespuesta = false, editar = true, clasificacion = null, subseccion} = props;

  const {control, getValues, setValue} = useFormContext();
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const showBlumarIncidentFields =
    !hideBlumarStuffForOthers || currentTenant?.id === BLUMAR_SSO_TENANT_ID;
  const incidentSubTableEditorOptions = INCIDENT_SUBTABLE_KIND_EDITOR_OPTIONS.filter(
    (opt) => showBlumarIncidentFields || opt.value !== 'entrevistas',
  );
  const clasificacionWatch = useWatch({name: 'clasificacion', control});
  const effectiveClasificacion =
    clasificacionWatch != null && clasificacionWatch !== ''
      ? clasificacionWatch
      : clasificacion != null && clasificacion !== ''
        ? clasificacion
        : null;

  const {fields, remove, append, insert, move} = useFieldArray({
    control,
    name: `${name}.${categoriaIndex}.subseccions`,
  });
  const fieldsWatch = useWatch({name: `${name}.${categoriaIndex}.subseccions`, control});

  const [recordToDelete, setRecordToDelete] = useState(null);

  const doCloseDeleteConfirmModal = () => {
    setRecordToDelete(null);
  };

  const doDeleteRecord = (subseccionIndex) => {
    remove(subseccionIndex);
    doCloseDeleteConfirmModal();
  };

  const doOpenDeleteConfirmModal = (subseccionIndex) => {
    setRecordToDelete(subseccionIndex);
  };

  const [recordToCopy, setRecordToCopy] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSubseccionIndex, setMenuSubseccionIndex] = useState<number | null>(null);
  const [hasClipboardSubsection, setHasClipboardSubsection] = useState(false);

  const doCloseCopyConfirmModal = () => {
    setRecordToCopy(null);
  };

  const doCopyJob = async (original) => {
    const subseccion = {...original};
    subseccion.categoria = subseccion.categoria + ' Copia ' + moment().format('DD-MM-YYYY HH:mm:ss');
    delete subseccion.key;
    delete subseccion.id;
    delete subseccion.subTableKind;
    delete subseccion.subTableConfig;
    // delete record._id;
    // delete record.tenant;
    // delete record.createdAt;
    // delete record.updatedAt;
    // delete record.createdBy;
    // delete record.updatedBy;
    //console.log('%c⧭ doCopyJob', 'color: #9c66cc', subseccion);
    append(subseccion);
    doCloseCopyConfirmModal();
  };

  const doOpenCopyConfirmModal = (subseccionIndex) => {
    const subseccion = getValues(`${name}.${categoriaIndex}.subseccions.${subseccionIndex}`);
    setRecordToCopy(subseccion);
  };
  void doOpenCopyConfirmModal;

  const normalizeAlternativas = (alternativas, depth = 1) => {
    if (!Array.isArray(alternativas)) {
      return [];
    }
    return alternativas
      .map((alt) => {
        const label = `${alt?.label ?? alt?.value ?? ''}`.trim();
        if (!label) {
          return null;
        }
        const normalized: any = {
          id: generateClipboardId(),
          label,
          children: [],
        };
        if (depth < 3 && Array.isArray(alt?.children)) {
          normalized.children = normalizeAlternativas(alt.children, depth + 1);
        }
        return normalized;
      })
      .filter(Boolean);
  };

  const stripSystemFields = (obj: any) => {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((entry) => stripSystemFields(entry));
    }

    const cloned = {...obj};
    delete cloned.id;
    delete cloned.key;
    delete cloned._id;
    delete cloned.tenant;
    delete cloned.createdAt;
    delete cloned.updatedAt;
    delete cloned.createdBy;
    delete cloned.updatedBy;

    Object.keys(cloned).forEach((key) => {
      cloned[key] = stripSystemFields(cloned[key]);
    });
    return cloned;
  };

  const normalizeSubsectionForPaste = (rawSubsection: any) => {
    const subsection = stripSystemFields(rawSubsection || {});
    subsection.categoria = subsection.categoria || '';
    subsection.preguntas = Array.isArray(subsection.preguntas) ? subsection.preguntas : [];

    subsection.preguntas = subsection.preguntas.map((pregunta, index) => {
      const normalizedQuestion: any = stripSystemFields(pregunta || {});
      normalizedQuestion.nPregunta = index + 1;
      normalizedQuestion.pregunta = {
        ...(normalizedQuestion.pregunta || {}),
        titulo: normalizedQuestion.pregunta?.titulo || normalizedQuestion.pregunta?.label || '',
        label: normalizedQuestion.pregunta?.label || normalizedQuestion.pregunta?.titulo || '',
      };
      normalizedQuestion.alternativas = normalizeAlternativas(normalizedQuestion.alternativas || []);
      normalizedQuestion.alternativasCheckbox = normalizeAlternativas(normalizedQuestion.alternativasCheckbox || []);
      return normalizedQuestion;
    });

    return subsection;
  };

  const doCopySubsectionToClipboard = async (subseccionIndex) => {
    const subsection = getValues(`${name}.${categoriaIndex}.subseccions.${subseccionIndex}`);
    const payload = {
      type: SUBSECTION_CLIPBOARD_TYPE,
      version: SUBSECTION_CLIPBOARD_VERSION,
      payload: {
        subseccion: normalizeSubsectionForPaste(subsection),
      },
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
      Message.success(i18n('common.copySubsectionClipboardSuccess'));
    } catch (error) {
      Message.error(i18n('common.copySubsectionClipboardError'));
    }
  };

  const getClipboardSubsection = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const parsed = JSON.parse(clipboardText);
      if (parsed?.type !== SUBSECTION_CLIPBOARD_TYPE || parsed?.version !== SUBSECTION_CLIPBOARD_VERSION) {
        return null;
      }
      return normalizeSubsectionForPaste(parsed?.payload?.subseccion);
    } catch (error) {
      return null;
    }
  };

  const doPasteSubsectionFromClipboard = async (subseccionIndex, direction: 'up' | 'down') => {
    const subsection = await getClipboardSubsection();
    if (!subsection) {
      Message.error(i18n('common.invalidSubsectionClipboard'));
      return;
    }
    const targetIndex = direction === 'up' ? subseccionIndex : subseccionIndex + 1;
    insert(targetIndex, subsection);
    Message.success(i18n('common.pasteSubsectionClipboardSuccess'));
  };

  const doOpenActionsMenu = async (event, subseccionIndex) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSubseccionIndex(subseccionIndex);
    const subsection = await getClipboardSubsection();
    setHasClipboardSubsection(!!subsection);
  };

  const doCloseActionsMenu = () => {
    setMenuAnchorEl(null);
    setMenuSubseccionIndex(null);
    setHasClipboardSubsection(false);
  };

  const doCopySubsectionMenuAction = async () => {
    if (menuSubseccionIndex === null) {
      return;
    }
    await doCopySubsectionToClipboard(menuSubseccionIndex);
    doCloseActionsMenu();
  };

  const doPasteSubsectionMenuAction = async (direction: 'up' | 'down') => {
    if (menuSubseccionIndex === null) {
      return;
    }
    await doPasteSubsectionFromClipboard(menuSubseccionIndex, direction);
    doCloseActionsMenu();
  };

  const doDeleteSubsectionMenuAction = () => {
    if (menuSubseccionIndex === null) {
      return;
    }
    doOpenDeleteConfirmModal(menuSubseccionIndex);
    doCloseActionsMenu();
  };

  useEffect(() => {
    if (esRespuesta) {
      // fields.map((item, subseccionIndex) => {
      //   register(
      //     `${name}[${categoriaIndex}].subseccions[${subseccionIndex}].categoria`,
      //   );
      //   setValue(
      //     `${name}[${categoriaIndex}].subseccions[${subseccionIndex}].categoria`,
      //     item.categoria,
      //   );
      // });
    }
  }, []);

  //console.log('%c⧭', 'color: #2a00e6', 'SubseccionesFormulario', name);

  const [expanded, setExpanded] = React.useState<string | false>(false); //fields?.[0]?.id);

  const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const moveUp = (index) => {
    if (index === 0) {
      return;
    }
    move(index, index - 1);
  };

  const moveDown = (index) => {
    if (index === fields.length - 1) {
      return;
    }
    move(index, index + 1);
  };

  const renderSubsectionSummary = (item, subseccionIndex) => {
    return (
      <div style={{display: 'inline-flex', width: '100%', cursor: 'default'}}>
        <div
          style={{
            width: '81%',
            //backgroundColor: 'rgb(255,255,255,.725)',
            borderRadius: '5px',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingBottom: '4px',
            marginBottom: 10,
          }}>
          <div onClick={($e) => $e.stopPropagation()}>
            <InputFormItem
              name={`${name}.${categoriaIndex}.subseccions.${subseccionIndex}.categoria`}
              defaultValue={item.categoria}
              label={'Subsección ' + (subseccionIndex + 1)}
              variant="outlined"
              fontSize="1.2rem"
              labelFontSize="1.08rem"
              shrinkLabel={false}
            />
          </div>
        </div>
        <OnHoverBox style={{marginLeft: 'auto', alignSelf: 'center'}} onClick={($e) => $e.stopPropagation()}>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            style={{marginLeft: 10}}
            followCursor
            placement={'top-start'}
            title={i18n('common.dynamicSubsection')}>
            <div>
              <CheckboxFormItem name={`${name}.${categoriaIndex}.subseccions.${subseccionIndex}.isDynamic`} label={''} />
            </div>
          </Tooltip>
          {isIncidentLinkedClasificacion(effectiveClasificacion) && (
            <Tooltip
              enterDelay={700}
              leaveDelay={150}
              enterNextDelay={400}
              followCursor
              placement={'top-start'}
              title="Filas dinámicas para ciertos campos de incidente">
              <FormControl
                size="small"
                sx={{minWidth: 168, ml: 0.5, mr: 0}}
                disabled={!editar || !Boolean(fieldsWatch?.[subseccionIndex]?.isDynamic)}>
                <InputLabel id={`subTableKind-label-${subseccionIndex}`}>Tabla de subsección (incidente)</InputLabel>
                <Select
                  labelId={`subTableKind-label-${subseccionIndex}`}
                  label="Tabla de subsección (incidente)"
                  value={String(fieldsWatch?.[subseccionIndex]?.subTableKind ?? '')}
                  onChange={(e) => {
                    const subPath = `${name}.${categoriaIndex}.subseccions.${subseccionIndex}`;
                    const v = String(e.target.value ?? '');
                    setValue(`${subPath}.subTableKind`, v, {shouldDirty: true});
                    if (!v) {
                      setValue(`${subPath}.subTableConfig`, {enabled: true, fields: []}, {shouldDirty: true});
                    } else {
                      setValue(`${subPath}.isTask`, false, {shouldDirty: true});
                      setValue(`${subPath}.taskConfig.fields`, [], {shouldDirty: true});
                      const def = getIncidentSubTableDef(v);
                      if (!def?.hasColumnConfigEditor) {
                        setValue(`${subPath}.subTableConfig`, {enabled: true, fields: []}, {shouldDirty: true});
                      }
                    }
                  }}>
                  {incidentSubTableEditorOptions.map((opt) => (
                    <MenuItem key={opt.value || 'none'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          )}
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            style={{marginLeft: -8, marginRight: -4}}
            followCursor
            placement={'top-start'}
            title={'Enviar Tarea'}>
            <div>
              <CheckboxFormItem
                name={`${name}.${categoriaIndex}.subseccions.${subseccionIndex}.isTask`}
                label={''}
                onChange={(checked) => {
                  const subPath = `${name}.${categoriaIndex}.subseccions.${subseccionIndex}`;
                  if (checked) {
                    setValue(`${subPath}.subTableKind`, '', {shouldDirty: true});
                    setValue(`${subPath}.subTableConfig`, {enabled: true, fields: []}, {shouldDirty: true});
                  } else {
                    const path = `${subPath}.taskConfig.fields`;
                    setValue(path, [], {shouldDirty: true});
                  }
                }}
              />
            </div>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            followCursor
            placement={'top-start'}
            title={i18n('common.moveDown')}>
            <IconButton size="small" color="primary" onClick={() => moveDown(subseccionIndex)}>
              <ArrowDownward />
            </IconButton>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            followCursor
            placement={'top-start'}
            title={i18n('common.moveUp')}>
            <IconButton size="small" color="primary" onClick={() => moveUp(subseccionIndex)}>
              <ArrowUpward />
            </IconButton>
          </Tooltip>
          <Tooltip
            enterDelay={700}
            leaveDelay={150}
            enterNextDelay={400}
            disableHoverListener={Boolean(menuAnchorEl)}
            placement={'top-start'}
            title={i18n('common.subsectionActions')}>
            <IconButton size="small" color="primary" onClick={(event) => doOpenActionsMenu(event, subseccionIndex)}>
              <MoreVert />
            </IconButton>
          </Tooltip>
        </OnHoverBox>
      </div>
    );
  };

  const renderSubsectionBeforeDetailTitle = (item, subseccionIndex) => (
    <div>
      <CheckboxFormItem
        label={i18n('common.allowNotApplicable')}
        name={`${name}.${categoriaIndex}.subseccions.${subseccionIndex}.allowNotApplicable`}
        size={'small'}
      />
    </div>
  );

  const renderSubsectionDetails = (item, subseccionIndex) => {
    return (
      <div>
        <SubseccionesPreguntasFormularioForm
          item={subseccion}
          removeSubseccion={remove}
          categoriaIndex={categoriaIndex}
          subseccionIndex={subseccionIndex}
          name={name}
          editar={editar}
          clasificacion={effectiveClasificacion}
        />
      </div>
    );
  };

  if (esRespuesta) {
    return (
      <Typography
        variant="h3"
        // sx={{ color: '#1E1F5A' }}
      >
        {subseccion.categoria}
      </Typography>
    );
  } else {
    return (
      <React.Fragment>
        {fieldsWatch?.length === 0 && (
          <Button
            onClick={() =>
              append({categoria: '', isDynamic: false, allowNotApplicable: false, subTableKind: '', preguntas: []})
            }
            variant="outlined"
            color="primary"
            type="button">
            + Añadir Subsección
          </Button>
          // <div style={{marginLeft: 'auto', alignSelf: 'center'}}>
          //   <IconButton
          //     color="primary"
          //     onClick={() => {
          //       append({categoria: '', preguntas: []});
          //     }}
          //     style={{backgroundColor: '#fff', border: '1px solid rgb(63, 81, 181,.6)'}}>
          //     <Add />
          //   </IconButton>
          // </div>
        )}
        {fields.map((subseccion: any, subseccionIndex) => (
          <CustomAccordion
            key={subseccion.id}
            item={subseccion}
            index={subseccionIndex}
            uniqueKey={subseccion.id}
            entityName="Subsección"
            renderSummary={renderSubsectionSummary}
            renderBeforeDetailTitle={renderSubsectionBeforeDetailTitle}
            renderDetails={renderSubsectionDetails}
            detailTitle={'Preguntas'}
            expanded={expanded}
            handleChange={handleChange}
            topButtonClick={(item, index) => {
              insert(index, {
                categoria: '',
                isDynamic: false,
                allowNotApplicable: false,
                subTableKind: '',
                isTask: false,
                taskConfig: {
                  enabled: true,
                  fields: [],
                  mapping: {},
                  defaults: {},
                },
                preguntas: [
                  {
                    nPregunta: null,
                    pregunta: {
                      titulo: null,
                    },
                    link: '',
                    tipoRespuesta: null,
                    tieneComentario: false,
                    tienePuntaje: false,
                  },
                ],
              });
            }}
            bottomButtonClick={(item, index) => {
              insert(index + 1, {
                categoria: '',
                isDynamic: false,
                allowNotApplicable: false,
                subTableKind: '',
                isTask: false,
                taskConfig: {
                  enabled: true,
                  fields: [],
                  mapping: {},
                  defaults: {},
                },
                preguntas: [
                  {
                    nPregunta: null,
                    pregunta: {
                      titulo: null,
                    },
                    link: '',
                    tipoRespuesta: null,
                    tieneComentario: false,
                    tienePuntaje: false,
                  },
                ],
              });
            }}
          />
        ))}

        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={doCloseActionsMenu}>
          <MenuItem onClick={doCopySubsectionMenuAction}>
            <FileCopy fontSize="small" style={{marginRight: 8}} />
            {i18n('common.copySubsectionClipboard')}
          </MenuItem>
          {hasClipboardSubsection && (
            <MenuItem onClick={() => doPasteSubsectionMenuAction('up')}>{i18n('common.pasteSubsectionAbove')}</MenuItem>
          )}
          {hasClipboardSubsection && (
            <MenuItem onClick={() => doPasteSubsectionMenuAction('down')}>{i18n('common.pasteSubsectionBelow')}</MenuItem>
          )}
          <MenuItem onClick={doDeleteSubsectionMenuAction}>
            <Delete fontSize="small" style={{marginRight: 8}} />
            {i18n('common.destroy')}
          </MenuItem>
        </Menu>

        {recordToCopy && (
          <ConfirmModal
            title={i18n('common.areYouSureCopySubseccion')}
            onConfirm={() => doCopyJob(recordToCopy)}
            onClose={() => doCloseCopyConfirmModal()}
            okText={i18n('common.yes')}
            cancelText={i18n('common.no')}
          />
        )}
        {(recordToDelete || recordToDelete === 0) && (
          <ConfirmModal
            title={i18n('common.areYouSureDelete')}
            onConfirm={() => doDeleteRecord(recordToDelete)}
            onClose={() => doCloseDeleteConfirmModal()}
            okText={i18n('common.yes')}
            cancelText={i18n('common.no')}
          />
        )}
      </React.Fragment>
    );
  }
}
